import { QR_TYPE } from 'features/generator/generatorTypes'
import { atom, useAtom, useAtomValue } from 'jotai'
import { atomWithReset } from 'jotai/utils'
import { replace } from 'radash'
import { useCallback, useRef } from 'react'
import {
	normalizeFileName,
	sanitizeFileName,
	TUploadedMedia,
	uploadFile,
} from 'services/mediaStorage'
import { sanitizeVideoUrl } from 'utils/sanitizeVideoUrl'

import type { ColorsDTO } from '../../services/types'
import { videoUrlAtom } from './VideoForm'
import { TVideo } from './VideoFormModel'
import { videoListAtom } from './VideoSection/VideoDetailsFields'

export interface VideoSourceDto {
	url: string | null
	uploaded: CloudinaryMediaDetailsDTO | null
}

export type ButtonDTO = {
	title: string
	url: string
}

export type VideoInfoDTO = {
	title: string
	description: string
	buttons: ButtonDTO[]
}

export enum TransformationStatusEnum {
	NOT_YET_APPLIED = 'not_yet_applied',
	TRANSFORMED = 'transformed',
	TRANSFORMED_FFMPEG = 'transformed_ffmpeg',
	NOT_APPLICABLE = 'not_applicable',
	FAILED = 'failed',
}

export type PlatformVideoDTO = {
	name: string
	description: string
	source: VideoSourceDto
}

export type CloudinaryMediaDetailsDTO = {
	publicId: string
	key?: string
	resourceType: string
	format: string
	bytes: number
	storageProvider?: string
	hasAutoTransformation?: TransformationStatusEnum
	bucketRootUrl?: string
}

export type VideoDTO = {
	type: QR_TYPE.Video
	name: string
	colors: ColorsDTO
	loaderImage: CloudinaryMediaDetailsDTO | null
	info: VideoInfoDTO
	videos: PlatformVideoDTO[]
	isShareable: boolean
}

export type VideoConfig = {
	id: string
	video: PlatformVideoDTO
	isUploaded?: boolean
}

const createVideoConfig = (
	source: string | CloudinaryMediaDetailsDTO,
	isUploaded: boolean,
): VideoConfig => {
	const baseConfig = {
		video: { name: '', source: {}, description: '' },
		id: Math.random().toString(),
		isUploaded,
	} as VideoConfig

	if (typeof source === 'string') {
		baseConfig.video.source.url = sanitizeVideoUrl(source)
	} else {
		baseConfig.video.source.uploaded = source
	}

	return baseConfig
}

export const progressAtom = atomWithReset({})

export const uploadingVideoFilesAtom = atom<File[]>([])

export const uploadFileToTvideo = (
	uploadedFile: TUploadedMedia,
	name: string,
	originalName: string,
) => {
	const newVideo = {
		video: {
			name: name,
			description: '',
			originalName: originalName,
			source: {
				uploaded: {
					publicId: uploadedFile.publicId,
					bucketRootUrl: uploadedFile.bucketRootUrl,
					bytes: uploadedFile.bytes,
					format: uploadedFile.format,
					hasAutoTransformation: uploadedFile.hasAutoTransformation,
					key: uploadedFile.key,
					resourceType: uploadedFile.resourceType,
					storageProvider: uploadedFile.storageProvider,
				},
				demo: undefined,
				url: '',
			},
		},
	}
	return newVideo
}

export const useVideo = () => {
	const [progress, setProgress] = useAtom(progressAtom)
	const [files, setFiles] = useAtom(uploadingVideoFilesAtom)

	const fileNameToUploadControllerRef = useRef<
		Record<string, AbortController>
	>({})
	const displayNameUsageCountsRef = useRef<Record<string, number>>({})

	const [videoList, setVideoList] = useAtom(videoListAtom)
	const videoUrl = useAtomValue(videoUrlAtom)

	const getUniqueDisplayName = (sanitizedFileName: string): string => {
		const usageCount =
			(displayNameUsageCountsRef.current[sanitizedFileName] ?? 0) + 1
		displayNameUsageCountsRef.current[sanitizedFileName] = usageCount

		if (usageCount === 1) return sanitizedFileName

		const lastDotIndex = sanitizedFileName.lastIndexOf('.')
		const base =
			lastDotIndex > 0
				? sanitizedFileName.substring(0, lastDotIndex)
				: sanitizedFileName
		const extension =
			lastDotIndex > 0 ? sanitizedFileName.substring(lastDotIndex) : ''

		return `${base} (${usageCount})${extension}`
	}

	const upload = async (file: File): Promise<boolean> => {
		const sanitizedFileName = sanitizeFileName(file.name)
		const displayFileName = getUniqueDisplayName(sanitizedFileName)
		const uniqueId =
			globalThis.crypto?.randomUUID?.() ||
			Math.random().toString(36).slice(2)
		const uniqueFileName = `${uniqueId}__${sanitizedFileName}`
		const normalizedFileName = normalizeFileName(uniqueFileName)

		const fileToUpload = new File([file], uniqueFileName, {
			type: file.type,
		})

		setFiles((prev: File[]) => [...prev, fileToUpload])

		setProgress((prev) => {
			const newValue = { ...prev } as Record<string, number>
			newValue[normalizedFileName] = 0
			return newValue
		})

		const emptyVideo = {
			video: {
				name: displayFileName,
				description: '',
				originalName: fileToUpload.name,
				source: {
					uploaded: null,
					demo: undefined,
					url: '',
				},
			},
		} as unknown as TVideo

		setVideoList((prev) => [...prev, emptyVideo])

		fileNameToUploadControllerRef.current[normalizedFileName] =
			new AbortController()
		const uploadedFile = await uploadFile(
			fileToUpload,
			(progress) => {
				setProgress((prev) => {
					const newValue = { ...prev } as Record<string, number>
					newValue[normalizedFileName] = progress
					return newValue
				})
			},
			fileNameToUploadControllerRef.current[normalizedFileName]!.signal,
			undefined,
			{ displayFileName: sanitizedFileName },
		)

		if (uploadedFile) {
			const newVideo = uploadFileToTvideo(
				uploadedFile,
				displayFileName,
				fileToUpload.name,
			)
			setVideoList((prev) =>
				replace(
					prev,
					newVideo as unknown as TVideo,
					(item: TVideo) =>
						item.video.originalName === fileToUpload.name,
				),
			)
			setProgress((prev) => {
				const newValue = { ...prev } as Record<string, number>
				newValue[normalizedFileName] = 100
				return newValue
			})
		}

		// Get the current progress state after the update
		let currentProgress: Record<string, number> = {}
		setProgress((prev) => {
			currentProgress = { ...prev } as Record<string, number>
			return prev
		})

		return Object.values(currentProgress).every((item) => item === 100)
	}

	const fileDelete = useCallback(
		(file: File, indexToReplace: number) => {
			const normalizedFileName = normalizeFileName(file.name)
			fileNameToUploadControllerRef.current[normalizedFileName]?.abort()
			delete fileNameToUploadControllerRef.current[normalizedFileName]

			const deletedFile = files[indexToReplace]
			setFiles((prev: File[]) =>
				[...prev].filter((_, index) => index !== indexToReplace),
			)
			setVideoList((prev) =>
				prev.filter(
					(item) => deletedFile?.name !== item.video.originalName,
				),
			)
			setProgress((prev) => {
				const newValue = { ...prev } as Record<string, number>
				delete newValue[normalizedFileName]
				return newValue
			})
		},
		[setVideoList, files, setProgress, setFiles],
	)

	const removeProgress = useCallback(
		(videoName: string) => {
			const normalizedVideoName = normalizeFileName(videoName)
			setProgress((prev) => {
				const newValue = { ...prev } as Record<string, number>
				delete newValue[normalizedVideoName]
				return newValue
			})
			setFiles((prev: File[]) =>
				[...prev].filter(
					(file) =>
						normalizeFileName(file.name) !== normalizedVideoName,
				),
			)
		},
		[setProgress, setFiles],
	)

	const add = useCallback(() => {
		if (videoList.length >= 10) return

		// @ts-expect-error TODO: Fix type error
		setVideoList((prev) => [...prev, createVideoConfig(videoUrl, false)])
	}, [setVideoList, videoList.length, videoUrl])

	return {
		setProgress,
		progress,
		upload,
		add,
		videoList,
		fileDelete,
		files,
		setFiles,
		removeProgress,
	}
}
